/**
 * Centralized Error Handler
 * Sanitizes errors before displaying to users
 * Logs detailed errors for debugging (development only)
 */

import { toast } from 'react-toastify';
import logger from './logger';

/**
 * Error type mappings to user-friendly messages
 *
 * IMPORTANT: Never expose technical details to users including:
 * - Encryption key status ("master key not found")
 * - Crypto operations ("decryption failed")
 * - Session storage details
 * - Database/server internals
 * - Configuration issues
 */
const ERROR_MESSAGES = {
  // Authentication errors
  INVALID_CREDENTIALS: 'Invalid email or password. Please try again.',
  PLAN_LIMIT: 'You have reached a limit of your current plan. Upgrade to Pro for more.',
  EMAIL_ALREADY_REGISTERED: 'Email already registered. Please sign in.',
  ACCOUNT_PENDING_APPROVAL: 'Your account is pending approval. Please wait.',
  ACCOUNT_REJECTED: 'Your account registration was rejected. Please contact support.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  ACCOUNT_LOCKED: 'Too many failed attempts. Please try again in 30 minutes.',
  MFA_REQUIRED: 'Please verify your identity with the code sent to your email.',
  MFA_INVALID: 'Invalid verification code. Please try again.',

  // Encryption/Session errors (NEVER reveal technical details)
  // All crypto/session errors map to generic session expiry
  ENCRYPTION_FAILED: 'Your session has expired. Please log in again.',
  DECRYPTION_FAILED: 'Your session has expired. Please log in again.',
  MASTER_KEY_MISSING: 'Your session has expired. Please log in again.',
  CRYPTO_ERROR: 'Your session has expired. Please log in again.',
  INVALID_PASSWORD: 'Incorrect password. Please try again.',

  // Data operations
  CREATE_FAILED: 'Failed to save. Please try again.',
  UPDATE_FAILED: 'Failed to update. Please try again.',
  DELETE_FAILED: 'Failed to delete. Please try again.',
  FETCH_FAILED: 'Failed to load data. Please refresh the page.',

  // Nominee errors
  NOMINEE_NOT_FOUND: 'Nominee not found. Please check with the vault owner.',
  ACTIVATION_FAILED: 'Activation failed. Please check your activation link.',
  INVITATION_INVALID: 'Invalid invitation key. Please try again.',

  // Payment errors
  PAYMENT_FAILED: 'Payment failed. Please try again or use a different method.',
  SUBSCRIPTION_ERROR: 'Failed to process subscription. Please contact support.',

  // File errors
  FILE_TOO_LARGE: 'File size exceeds the limit. Please choose a smaller file.',
  FILE_TYPE_INVALID: 'File type not allowed for security reasons.',
  DOWNLOAD_FAILED: 'Failed to download file. Please try again.',

  // Network errors
  NETWORK_ERROR: 'Connection error. Please check your internet and try again.',
  SERVER_ERROR: 'Service temporarily unavailable. Please try again later.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',

  // Validation errors
  VALIDATION_ERROR: 'Please check your input and try again.',
  REQUIRED_FIELD: 'Please fill in all required fields.',

  // Generic fallback (never reveal why it failed)
  UNKNOWN_ERROR: 'Something went wrong. Please try again.'
};

/**
 * Determines error category from error object or message
 */
function categorizeError(error) {
  // Check for network errors
  if (!error.response && error.request) {
    return 'NETWORK_ERROR';
  }

  // Check for timeout
  if (error.code === 'ECONNABORTED') {
    return 'TIMEOUT_ERROR';
  }

  // Check HTTP status codes
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data || {};
    const detail = data.detail || '';

    switch (status) {
      case 400:
        if (detail.toLowerCase().includes('email already registered')) {
          return 'EMAIL_ALREADY_REGISTERED';
        }
        break;

      case 401:
        if (detail.toLowerCase().includes('session')) {
          return 'SESSION_EXPIRED';
        }
        if (detail.toLowerCase().includes('mfa')) {
          return 'MFA_INVALID';
        }
        return 'INVALID_CREDENTIALS';

      case 403:
        if (detail.toLowerCase().includes('pending') && detail.toLowerCase().includes('approval')) {
          return 'ACCOUNT_PENDING_APPROVAL';
        }
        if (detail.toLowerCase().includes('rejected')) {
          return 'ACCOUNT_REJECTED';
        }
        // Subscription tier-limit rejections (tier-enforcement.ts / nominee setup)
        // carry an upgrade prompt — never mask them as a credentials error.
        if (detail.toLowerCase().includes('plan') ||
            detail.toLowerCase().includes('upgrade') ||
            detail.toLowerCase().includes('limit')) {
          return 'PLAN_LIMIT';
        }
        return 'INVALID_CREDENTIALS';

      case 404:
        if (detail.toLowerCase().includes('nominee')) {
          return 'NOMINEE_NOT_FOUND';
        }
        return 'FETCH_FAILED';

      case 429:
        return 'ACCOUNT_LOCKED';

      case 500:
      case 502:
      case 503:
        return 'SERVER_ERROR';

      default:
        break;
    }
  }

  // Check error message content
  // CRITICAL: Never expose technical error messages to users
  const message = error.message || '';
  const lowerMessage = message.toLowerCase();

  // All crypto/key/session errors = session expired (never reveal details)
  if (lowerMessage.includes('master key') ||
      lowerMessage.includes('encryption key') ||
      lowerMessage.includes('session expired') ||
      lowerMessage.includes('session key') ||
      lowerMessage.includes('dek') ||
      lowerMessage.includes('kek') ||
      lowerMessage.includes('wrapped') ||
      lowerMessage.includes('unwrap')) {
    return 'SESSION_EXPIRED';
  }

  // All encryption/decryption errors = session expired (never reveal crypto operations)
  if (lowerMessage.includes('decrypt') ||
      lowerMessage.includes('encrypt') ||
      lowerMessage.includes('cipher') ||
      lowerMessage.includes('crypto')) {
    return 'CRYPTO_ERROR';
  }

  // Password errors (only if explicitly about wrong password)
  if (lowerMessage.includes('incorrect password') || lowerMessage.includes('wrong password')) {
    return 'INVALID_PASSWORD';
  }

  if (lowerMessage.includes('network')) {
    return 'NETWORK_ERROR';
  }

  if (lowerMessage.includes('nominee')) {
    return 'NOMINEE_NOT_FOUND';
  }

  if (lowerMessage.includes('payment')) {
    return 'PAYMENT_FAILED';
  }

  if (lowerMessage.includes('file') && lowerMessage.includes('size')) {
    return 'FILE_TOO_LARGE';
  }

  if (lowerMessage.includes('executable') || lowerMessage.includes('not allowed')) {
    return 'FILE_TYPE_INVALID';
  }

  return 'UNKNOWN_ERROR';
}

/**
 * Sanitizes error and returns user-friendly message
 */
export function getSafeErrorMessage(error, customMessage = null) {
  // If custom message provided, use it
  if (customMessage) {
    return customMessage;
  }

  // Categorize and map to safe message
  const category = categorizeError(error);

  // Plan-limit details are server-authored upgrade prompts (no sensitive
  // internals) — show them verbatim so the user sees the actual limit.
  if (category === 'PLAN_LIMIT') {
    return error.response?.data?.detail || ERROR_MESSAGES.PLAN_LIMIT;
  }

  return ERROR_MESSAGES[category] || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Handles error by logging (dev) and showing safe message (user)
 *
 * @param {Error} error - The error object
 * @param {string} context - Context where error occurred (e.g., 'login', 'create-credential')
 * @param {string} customMessage - Optional custom message to show user
 * @param {boolean} showToast - Whether to show toast notification (default: true)
 * @param {boolean} silent - If true, only logs error without showing toast (default: false)
 */
export function handleError(error, context = 'unknown', customMessage = null, showToast = true, silent = false) {
  // Log detailed error in development only
  // NEVER log sensitive data (passwords, keys, tokens)
  logger.error(`[${context}] Error:`, {
    message: error.message,
    response: error.response?.data,
    status: error.response?.status,
    // Don't log full stack trace with potentially sensitive data
    errorType: error.name
  });

  // Get safe message for user
  const safeMessage = getSafeErrorMessage(error, customMessage);

  // Show toast notification if requested and not silent
  if (showToast && !silent) {
    toast.error(safeMessage);
  }

  return safeMessage;
}

/**
 * Handles validation errors specifically
 */
export function handleValidationError(errors, showToast = true) {
  if (Array.isArray(errors)) {
    errors.forEach(error => {
      logger.error('Validation error:', error);
      if (showToast) {
        toast.error(error);
      }
    });
  } else {
    logger.error('Validation error:', errors);
    if (showToast) {
      toast.error(errors);
    }
  }
}

/**
 * Safe wrapper for async operations
 * Automatically handles errors without exposing details
 */
export async function safeAsync(asyncFn, context, customErrorMessage = null) {
  try {
    return await asyncFn();
  } catch (error) {
    handleError(error, context, customErrorMessage);
    throw error; // Re-throw so calling code can handle if needed
  }
}

/**
 * Creates a safe error handler for specific contexts
 * Returns a configured error handler function
 */
export function createErrorHandler(context, defaultMessage = null) {
  return (error, customMessage = null, showToast = true) => {
    return handleError(error, context, customMessage || defaultMessage, showToast);
  };
}

export default {
  handle: handleError,
  getSafeMessage: getSafeErrorMessage,
  handleValidation: handleValidationError,
  safeAsync,
  createHandler: createErrorHandler
};
